import type { ReactNode } from 'react';
import React, { useMemo, useRef } from 'react';
import { useStyletron } from 'baseui';
import { ColumnStrip } from './column-strip';
import { GRID_Z_INDEX, VIRTUAL_AXIS } from './constants';
import { StickyLeftCell } from './sticky-left-cell';
import type { VirtualGridPropsT } from './types';
import { useVirtualGrid } from './use-virtual-grid';
import { createOverlayControls } from './utils';
import { VirtualTrack } from './virtual-track';
const identityKey = (index: number) => index;
export const VirtualGrid = ({
  row,
  column,
  renderCorner,
  renderCell,
  renderOverlay
}: VirtualGridPropsT) => {
  const [css, theme] = useStyletron();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const {
    rowVirtualizer,
    columnVirtualizer
  } = useVirtualGrid({
    row,
    column,
    scrollRef
  });
  const overlayControls = useMemo(() => createOverlayControls({
    columnVirtualizer,
    rowVirtualizer
  }), [columnVirtualizer, rowVirtualizer]);
  const rowHeaderWidth = row.headerSize;
  const columnHeaderHeight = column.headerSize;
  const bodyWidth = columnVirtualizer.getTotalSize();
  const bodyHeight = rowVirtualizer.getTotalSize();
  const totalWidth = rowHeaderWidth + bodyWidth;
  const totalHeight = columnHeaderHeight + bodyHeight;
  const getRowKey = row.getKey ?? identityKey;
  const getColumnKey = column.getKey ?? identityKey;
  const renderColumnStrip = (renderCellAt: (columnIndex: number) => ReactNode) => <ColumnStrip width={bodyWidth} items={columnVirtualizer.getVirtualItems()} scrollMargin={columnVirtualizer.options.scrollMargin} getKey={getColumnKey} renderCell={renderCellAt} />;
  return <div ref={scrollRef} className={css({
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'auto',
    overscrollBehavior: 'none',
    /* Trap the sticky header/pinned-cell z-indexes in their own stacking context so
     overlays rendered after the grid (divider, tooltips) sit on top via DOM order alone. */
    isolation: 'isolate'
  })}>
      <div className={css({
      position: 'sticky',
      top: '0px',
      zIndex: GRID_Z_INDEX.headerRow,
      display: 'flex',
      width: `${totalWidth}px`,
      height: `${columnHeaderHeight}px`,
      boxSizing: 'border-box',
      borderBottom: `solid ${theme.sizing.scale0} ${theme.colors.borderOpaque}`,
      // Opaque so scrolling body rows pass behind the sticky header instead of through it.
      backgroundColor: theme.colors.backgroundPrimary
    })}>
        <StickyLeftCell width={rowHeaderWidth}>{renderCorner?.()}</StickyLeftCell>
        {renderColumnStrip(columnIndex => column.renderHeader?.(columnIndex))}
      </div>

      <div className={css({
      position: 'relative',
      width: `${totalWidth}px`,
      height: `${bodyHeight}px`
    })}>
        <VirtualTrack items={rowVirtualizer.getVirtualItems()} axis={VIRTUAL_AXIS.row} scrollMargin={rowVirtualizer.options.scrollMargin} getKey={getRowKey} renderItem={rowIndex => <div className={css({
        boxSizing: 'border-box',
        display: 'flex',
        width: '100%',
        height: '100%',
        // One continuous divider across the whole row (sticky cell + body + edge padding).
        borderBottom: `${theme.sizing.scale0} solid ${theme.colors.borderOpaque}`
      })}>
              <StickyLeftCell width={rowHeaderWidth}>{row.renderHeader?.(rowIndex)}</StickyLeftCell>
              {renderColumnStrip(columnIndex => renderCell(rowIndex, columnIndex))}
            </div>} />
      </div>

      {renderOverlay ? <div className={css({
      position: 'absolute',
      top: '0px',
      left: `${rowHeaderWidth}px`,
      height: `${totalHeight}px`,
      minHeight: '100%',
      zIndex: GRID_Z_INDEX.headerRow,
      pointerEvents: 'none'
    })}>
          {renderOverlay(overlayControls)}
        </div> : null}
    </div>;
};